/**
 * 统一查询区域组件
 * 检索条件自动从左到右排列，不足一排自动换行
 * 查询和重置按钮在底部操作区域
 */

import React from 'react';
import { Button, Input, Select, DatePicker, ConfigProvider } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { DictSelect } from '../../../../components/common/DictSelect';
import zhCN from 'antd/locale/zh_CN';
import './QueryArea.css';

const { RangePicker } = DatePicker;

export interface QueryField {
  key: string;
  label: string;
  type: 'input' | 'select' | 'dict' | 'dateRange';
  placeholder?: string;
  dictType?: string;
  options?: Array<{ label: string; value: string }>;
  width?: number;
}

export interface QueryAreaProps {
  fields: QueryField[];
  values: Record<string, any>;
  onValuesChange: (values: Record<string, any>) => void;
  onSearch?: () => void;
  onReset?: () => void;
  onAdd?: () => void;
  onBatchDelete?: () => void;
  batchDeleteDisabled?: boolean;
  selectedCount?: number;
  addButtonText?: string;
  batchDeleteButtonText?: string;
  // 自定义按钮配置
  customButtons?: Array<{
    key: string;
    text: string;
    icon?: React.ReactNode;
    type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
    danger?: boolean;
    loading?: boolean;
    disabled?: boolean;
    onClick: () => void;
  }>;
}

const QueryArea: React.FC<QueryAreaProps> = ({
  fields,
  values,
  onValuesChange,
  onSearch,
  onReset,
  onAdd,
  onBatchDelete,
  batchDeleteDisabled = false,
  selectedCount = 0,
  addButtonText = '新增',
  batchDeleteButtonText = '批量删除',
  customButtons = []
}) => {
  // 显示所有字段，使用flex自动换行布局
  const visibleFields = fields;

  const handleFieldChange = (key: string, value: any) => {
    onValuesChange({ ...values, [key]: value });
  };

  const renderField = (field: QueryField) => {
    const fieldValue = values[field.key];

    const commonProps = {
      placeholder: field.placeholder,
      style: { width: field.width || 200, minWidth: 200, maxWidth: 200 },
      value: fieldValue,
      onChange: (value: any) => handleFieldChange(field.key, value),
    };

    switch (field.type) {
      case 'input':
        return (
          <Input
            placeholder={field.placeholder}
            style={{ width: field.width || 200, minWidth: 200, maxWidth: 200 }}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            allowClear
          />
        );
      case 'select':
        return (
          <Select {...commonProps} allowClear>
            {field.options?.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );
      case 'dict':
        return (
          <DictSelect
            dictType={field.dictType!}
            {...commonProps}
            allowClear
          />
        );
      case 'dateRange':
        return (
          <RangePicker
            placeholder={['开始时间', '结束时间']}
            format="YYYY-MM-DD"
            style={{ width: field.width || 200, minWidth: 200, maxWidth: 200 }}
            value={fieldValue}
            onChange={(value: any) => handleFieldChange(field.key, value)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ConfigProvider locale={zhCN}>
      <div className="query-area">
      {/* 查询条件区域 */}
      <div className="query-conditions">
        {visibleFields.map((field) => (
          <div key={field.key} className="query-field">
            <label className="query-label">{field.label}:</label>
            <div className="query-input">
              {renderField(field)}
            </div>
          </div>
        ))}
      </div>

      {/* 操作按钮区域 */}
      <div className="query-actions-bottom">
        <div className="action-left">
          {onAdd && (
            <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
              {addButtonText}
            </Button>
          )}
          {onBatchDelete && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={onBatchDelete}
              disabled={batchDeleteDisabled}
            >
              {batchDeleteButtonText} {selectedCount > 0 && `(${selectedCount})`}
            </Button>
          )}
          {/* 自定义按钮 */}
          {customButtons.map((button) => (
            <Button
              key={button.key}
              type={button.type || 'default'}
              danger={button.danger}
              loading={button.loading}
              disabled={button.disabled}
              icon={button.icon}
              onClick={button.onClick}
            >
              {button.text}
            </Button>
          ))}
        </div>
        <div className="action-right">
          {onReset && (
            <Button icon={<ReloadOutlined />} onClick={onReset}>
              重置
            </Button>
          )}
          {onSearch && (
            <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
              查询
            </Button>
          )}
        </div>
      </div>
      </div>
    </ConfigProvider>
  );
};

export default QueryArea;

